import { createFileRoute } from "@tanstack/react-router";
import { AiChatNotice } from "@/components/safety/SafetyNotices";
import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";


export const Route = createFileRoute("/guide")({
  head: () => ({
    meta: [
      { title: "Recovery Trauma™ Guide — AI Companion" },
      {
        name: "description",
        content:
          "An AI companion for gentle reflection and finding your way around Recovery Trauma™. Not a therapist, not a crisis service and not monitored by a person.",
      },
      { property: "og:title", content: "Recovery Trauma™ Guide — AI Companion" },
      { property: "og:description", content: "An AI companion for gentle reflection and finding your way around Recovery Trauma™. Not a therapist, not a crisis service and not monitored by a person." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/guide" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/guide" }],
  }),
  component: GuidePage,
});

type Msg = { role: "user" | "assistant"; content: string };

const STARTERS = [
  "I'm feeling overwhelmed right now",
  "I can't sleep and my mind is racing",
  "Why do I keep shutting down?",
  "Help me reset — I'm panicking",
];

function GuidePage() {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  async function send(text: string) {
    const content = text.trim();
    if (!content || loading) return;
    setError(null);
    const next: Msg[] = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setError("The Guide is in demo mode — chat is unavailable without a backend session.");
        setLoading(false);
        return;
      }
      const resp = await fetch("/api/chat-guide", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ messages: next }),
      });


      if (!resp.ok || !resp.body) {
        const j = await resp.json().catch(() => ({ error: "Something went wrong." }));
        throw new Error(j.error || "Request failed");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      let opened = false;

      const pushAssistant = (chunk: string) => {
        acc += chunk;
        setMessages((prev) => {
          if (!opened) {
            opened = true;
            return [...prev, { role: "assistant", content: acc }];
          }
          const copy = prev.slice();
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      };

      let done = false;
      while (!done) {
        const { value, done: d } = await reader.read();
        if (d) break;
        buf += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf("\n")) !== -1) {
          let line = buf.slice(0, nl);
          buf = buf.slice(nl + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") {
            done = true;
            break;
          }
          try {
            const parsed = JSON.parse(json);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) pushAssistant(delta);
          } catch {
            buf = line + "\n" + buf;
            break;
          }
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col px-4 py-8 md:py-12" style={{ minHeight: "calc(100vh - 80px)" }}>
      <header className="mb-6 text-center">
        <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-gold/40 to-accent/40 ring-1 ring-gold/40">
          <Sparkles className="h-6 w-6 text-gold" />
        </div>
        <h1 className="font-display text-4xl tracking-tight md:text-5xl">Recovery Trauma™ Guide</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          An AI companion for gentle reflection and finding your way around Recovery Trauma™. It is AI, not a person — not a therapist, not medical advice and not a crisis service.
        </p>
      </header>

      <div
        ref={scrollRef}
        className="flex-1 space-y-4 overflow-y-auto rounded-2xl border border-border/50 bg-card/50 p-4 md:p-6"
      >
        {messages.length === 0 && (
          <div className="space-y-4">
            <p className="text-center text-sm text-muted-foreground">
              Share what's here. There's no wrong way to begin.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="rounded-xl border border-border/60 bg-background/60 px-4 py-3 text-start text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-gold/20 text-foreground ring-1 ring-gold/30"
                  : "bg-secondary/60 text-foreground"
              }`}
            >
              {m.content || <span className="opacity-60">…</span>}
            </div>
          </div>
        ))}

        {loading && messages[messages.length - 1]?.role === "user" && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 rounded-2xl bg-secondary/60 px-4 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              gathering a thought…
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}
      </div>

      <AiChatNotice className="mt-3" />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="mt-4 flex items-end gap-2"
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(input);
            }
          }}
          rows={1}
          placeholder="What's here right now?"
          className="flex-1 resize-none rounded-2xl border border-border/60 bg-background/80 px-4 py-3 text-sm focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="btn-gold flex h-11 w-11 items-center justify-center rounded-2xl disabled:opacity-50"
          aria-label="Send"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>

      <p className="mt-3 text-center text-xs text-muted-foreground">
        If you're in crisis, please call 988 (US) or visit{" "}
        <a href="/resources" className="underline">crisis resources</a>.
      </p>
    </div>
  );
}
